<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { UiText, UiIcon, UiSelect, UiCurrencyInput, UiSwitch, UiButton, UiDatePicker } from "@shopexperts/shared";
import type {
  AdminWalletTransactionsQueryParams,
  WalletTransactionDirection,
  WalletTransactionKind,
} from "@shopexperts/legacy-api";
import { centsToDollars, dollarsToCents } from "@/app/composables/expert-levels/expertLevelForm";

const props = defineProps<{
  /** Show the organization id filter (used on the global cross-org feed). */
  showOrganization?: boolean;
}>();

const model = defineModel<AdminWalletTransactionsQueryParams>({ default: () => ({}) });

const ANY = "";

const kindOptions: { label: string; value: string }[] = [
  { label: "Any type", value: ANY },
  { label: "Quote settlement", value: "expert_quote_settlement" },
  { label: "Admin credit", value: "admin_credit" },
  { label: "Admin debit", value: "admin_debit" },
  { label: "Admin hold", value: "admin_hold" },
  { label: "Admin hold debit", value: "admin_hold_debit" },
  { label: "External payment", value: "admin_external_payment" },
  { label: "Gross sales credit", value: "admin_gross_sales_credit" },
  { label: "Gross sales debit", value: "admin_gross_sales_debit" },
  { label: "Net credit", value: "admin_net_credit" },
  { label: "Net debit", value: "admin_net_debit" },
  { label: "Hold release", value: "hold_release" },
  { label: "Refund", value: "refund" },
  { label: "Refund from hold", value: "refund_from_hold" },
];

const directionOptions: { label: string; value: string }[] = [
  { label: "Any direction", value: ANY },
  { label: "Credit", value: "credit" },
  { label: "Debit", value: "debit" },
  { label: "Transfer", value: "transfer" },
];

const showAdvanced = ref(false);

type DatePickerValue = string | { year: number; month: number; day: number } | DatePickerValue[];

interface FilterState {
  search: string;
  transaction_kind: string;
  direction: string;
  eligible_for_hold_release: boolean;
  currency: string;
  created_from: DatePickerValue;
  created_to: DatePickerValue;
  amount_min_dollars: number;
  amount_max_dollars: number;
  organization_id: string;
  quote_id: string;
  project_id: string;
  related_transaction_id: string;
  expert_level_id: string;
  quote_payment_event_id: string;
  payment_provider: string;
  provider_payment_intent_id: string;
  provider_payment_link_id: string;
  idempotency_key: string;
}

const state = reactive<FilterState>({
  search: "",
  transaction_kind: ANY,
  direction: ANY,
  eligible_for_hold_release: false,
  currency: "",
  created_from: "",
  created_to: "",
  amount_min_dollars: 0,
  amount_max_dollars: 0,
  organization_id: "",
  quote_id: "",
  project_id: "",
  related_transaction_id: "",
  expert_level_id: "",
  quote_payment_event_id: "",
  payment_provider: "",
  provider_payment_intent_id: "",
  provider_payment_link_id: "",
  idempotency_key: "",
});

function toNumber(value: string | number): number | undefined {
  const trimmed = String(value).trim();
  if (trimmed === "") return undefined;
  const num = Number(trimmed);
  return Number.isFinite(num) ? num : undefined;
}

function toText(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

function toDateText(value: DatePickerValue): string | undefined {
  if (Array.isArray(value)) return value[0] ? toDateText(value[0]) : undefined;
  if (typeof value === "string") return toText(value);
  if (!Number.isFinite(value.year) || !Number.isFinite(value.month) || !Number.isFinite(value.day)) return undefined;
  const year = String(value.year).padStart(4, "0");
  const month = String(value.month).padStart(2, "0");
  const day = String(value.day).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const builtFilters = computed<AdminWalletTransactionsQueryParams>(() => {
  const filters: AdminWalletTransactionsQueryParams = {};
  if (toText(state.search)) filters.search = state.search.trim();
  if (state.transaction_kind) filters.transaction_kind = state.transaction_kind as WalletTransactionKind;
  if (state.direction) filters.direction = state.direction as WalletTransactionDirection;
  if (state.eligible_for_hold_release) filters.eligible_for_hold_release = true;
  if (toText(state.currency)) filters.currency = state.currency.trim().toUpperCase();
  if (toDateText(state.created_from)) filters.created_from = toDateText(state.created_from);
  if (toDateText(state.created_to)) filters.created_to = toDateText(state.created_to);
  if (Number.isFinite(state.amount_min_dollars) && state.amount_min_dollars > 0) {
    filters.amount_min_cents = dollarsToCents(state.amount_min_dollars);
  }
  if (Number.isFinite(state.amount_max_dollars) && state.amount_max_dollars > 0) {
    filters.amount_max_cents = dollarsToCents(state.amount_max_dollars);
  }
  if (props.showOrganization && toNumber(state.organization_id) !== undefined) {
    filters.organization_id = toNumber(state.organization_id);
  }
  if (toNumber(state.quote_id) !== undefined) filters.quote_id = toNumber(state.quote_id);
  if (toNumber(state.project_id) !== undefined) filters.project_id = toNumber(state.project_id);
  if (toNumber(state.related_transaction_id) !== undefined)
    filters.related_transaction_id = toNumber(state.related_transaction_id);
  if (toNumber(state.expert_level_id) !== undefined) filters.expert_level_id = toNumber(state.expert_level_id);
  if (toNumber(state.quote_payment_event_id) !== undefined)
    filters.quote_payment_event_id = toNumber(state.quote_payment_event_id);
  if (toText(state.payment_provider)) filters.payment_provider = state.payment_provider.trim();
  if (toText(state.provider_payment_intent_id))
    filters.provider_payment_intent_id = state.provider_payment_intent_id.trim();
  if (toText(state.provider_payment_link_id))
    filters.provider_payment_link_id = state.provider_payment_link_id.trim();
  if (toText(state.idempotency_key)) filters.idempotency_key = state.idempotency_key.trim();
  return filters;
});

const activeCount = computed(() => Object.keys(builtFilters.value).length);

// Seed the controls from an externally-provided model (e.g. filters restored
// from the URL on back-navigation) so the inputs reflect the active query.
function seedFromModel(value: AdminWalletTransactionsQueryParams | undefined) {
  if (!value) return;
  state.search = value.search ?? "";
  state.transaction_kind = value.transaction_kind ?? ANY;
  state.direction = value.direction ?? ANY;
  state.eligible_for_hold_release = value.eligible_for_hold_release === true;
  state.currency = value.currency ?? "";
  state.created_from = value.created_from ?? "";
  state.created_to = value.created_to ?? "";
  state.amount_min_dollars =
    value.amount_min_cents != null ? centsToDollars(value.amount_min_cents) : 0;
  state.amount_max_dollars =
    value.amount_max_cents != null ? centsToDollars(value.amount_max_cents) : 0;
  state.organization_id = value.organization_id != null ? String(value.organization_id) : "";
  state.quote_id = value.quote_id != null ? String(value.quote_id) : "";
  state.project_id = value.project_id != null ? String(value.project_id) : "";
  state.related_transaction_id =
    value.related_transaction_id != null ? String(value.related_transaction_id) : "";
  state.expert_level_id = value.expert_level_id != null ? String(value.expert_level_id) : "";
  state.quote_payment_event_id =
    value.quote_payment_event_id != null ? String(value.quote_payment_event_id) : "";
  state.payment_provider = value.payment_provider ?? "";
  state.provider_payment_intent_id = value.provider_payment_intent_id ?? "";
  state.provider_payment_link_id = value.provider_payment_link_id ?? "";
  state.idempotency_key = value.idempotency_key ?? "";
}

const ADVANCED_KEYS: (keyof AdminWalletTransactionsQueryParams)[] = [
  "currency",
  "created_from",
  "created_to",
  "amount_min_cents",
  "amount_max_cents",
  "organization_id",
  "quote_id",
  "project_id",
  "related_transaction_id",
  "expert_level_id",
  "quote_payment_event_id",
  "payment_provider",
  "provider_payment_intent_id",
  "provider_payment_link_id",
  "idempotency_key",
];

onMounted(() => {
  seedFromModel(model.value);
  // Open the advanced panel if any advanced filter was restored.
  if (model.value && ADVANCED_KEYS.some((key) => model.value?.[key] != null)) {
    showAdvanced.value = true;
  }
});

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
watch(
  builtFilters,
  (next) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      model.value = next;
    }, 250);
  },
  { deep: true },
);

function clearAll() {
  state.search = "";
  state.transaction_kind = ANY;
  state.direction = ANY;
  state.eligible_for_hold_release = false;
  state.currency = "";
  state.created_from = "";
  state.created_to = "";
  state.amount_min_dollars = 0;
  state.amount_max_dollars = 0;
  state.organization_id = "";
  state.quote_id = "";
  state.project_id = "";
  state.related_transaction_id = "";
  state.expert_level_id = "";
  state.quote_payment_event_id = "";
  state.payment_provider = "";
  state.provider_payment_intent_id = "";
  state.provider_payment_link_id = "";
  state.idempotency_key = "";
}

const idInputClass =
  "h-9 w-full rounded-md border border-se-gray-300 bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-se-primary";
</script>

<template>
  <div class="flex flex-col gap-4 rounded-xl border border-se-gray-200 bg-white p-4">
    <!-- Primary row -->
    <div class="flex flex-wrap items-end gap-3">
      <div class="relative min-w-[220px] flex-1">
        <div class="absolute left-3 top-1/2 -translate-y-1/2">
          <UiIcon name="search" class="h-5 w-5 text-se-gray-500" />
        </div>
        <input
          v-model="state.search"
          type="text"
          placeholder="Search transactions, notes, references…"
          class="h-9 w-full rounded-md border border-se-gray-300 bg-white py-1 pl-10 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-se-primary"
        />
      </div>

      <div class="w-[190px]">
        <UiSelect v-model="state.transaction_kind" :options="kindOptions" placeholder="Any type" size="md" />
      </div>
      <div class="w-[170px]">
        <UiSelect v-model="state.direction" :options="directionOptions" placeholder="Any direction" size="md" />
      </div>

      <div class="flex items-center gap-2 pb-1">
        <UiSwitch v-model="state.eligible_for_hold_release" />
        <UiText as="span" variant="p-small" class="text-se-gray-700" text="Releasable holds" />
      </div>

      <UiButton
        variant="ghost"
        size="sm"
        :label="showAdvanced ? 'Fewer filters' : 'More filters'"
        :icon="showAdvanced ? 'minimize-01' : 'filter'"
        @click="showAdvanced = !showAdvanced"
      />
      <UiButton
        v-if="activeCount > 0"
        variant="ghost"
        size="sm"
        label="Clear"
        icon="close"
        @click="clearAll"
      />
    </div>

    <!-- Advanced row -->
    <div v-if="showAdvanced" class="grid grid-cols-1 gap-4 border-t border-se-gray-200 pt-4 sm:grid-cols-2 lg:grid-cols-3">
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Created from" />
        <UiDatePicker v-model="state.created_from" placeholder="Select start date" size="md" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Created to" />
        <UiDatePicker v-model="state.created_to" placeholder="Select end date" size="md" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Currency" />
        <input v-model="state.currency" type="text" placeholder="USD" :class="idInputClass" />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Min amount" />
        <UiCurrencyInput v-model="state.amount_min_dollars" :min="0" :step="1" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Max amount" />
        <UiCurrencyInput v-model="state.amount_max_dollars" :min="0" :step="1" />
      </div>
      <div v-if="showOrganization" class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Organization id" />
        <input v-model="state.organization_id" type="number" placeholder="e.g. 123" :class="idInputClass" />
      </div>

      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Quote id" />
        <input v-model="state.quote_id" type="number" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Project id" />
        <input v-model="state.project_id" type="number" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Related transaction id" />
        <input v-model="state.related_transaction_id" type="number" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Expert level id" />
        <input v-model="state.expert_level_id" type="number" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Quote payment event id" />
        <input v-model="state.quote_payment_event_id" type="number" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Payment provider" />
        <input v-model="state.payment_provider" type="text" placeholder="stripe" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Provider intent id" />
        <input v-model="state.provider_payment_intent_id" type="text" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Provider payment link id" />
        <input v-model="state.provider_payment_link_id" type="text" :class="idInputClass" />
      </div>
      <div class="flex flex-col gap-1.5">
        <UiText as="label" variant="label" text="Idempotency key" />
        <input v-model="state.idempotency_key" type="text" :class="idInputClass" />
      </div>
    </div>
  </div>
</template>
